type Breakpoints = {
  min: {
    sm: string;
    md: string;
    lg: string;
    xl: string;
  };
  max: {
    sm: string;
    md: string;
    lg: string;
    xl: string;
  };
};

interface TypographyItem {
  fontFamily: string;
  fontWeight: number;
  fontSize: string;
  lineHeight: string;
  letterSpacing: string;
}

type Typography = {
  mobile: {
    heading1: TypographyItem;
    heading2: TypographyItem;
    heading3: TypographyItem;
    heading4: TypographyItem;
    heading5: TypographyItem;
    heading6: TypographyItem;
    body1: TypographyItem;
    body2: TypographyItem;
    body3: TypographyItem;
  };
  desktop: {
    heading1: TypographyItem;
    heading2: TypographyItem;
    heading3: TypographyItem;
    heading4: TypographyItem;
    heading5: TypographyItem;
    heading6: TypographyItem;
    body1: {
      fontFamily: string
      fontWeight: number;
      fontSize: string;
      lineHeight: string;
      letterSpacing: string;
    };
    body2: TypographyItem;
    body3: TypographyItem;
    body4: TypographyItem;
    body5: TypographyItem;
    link1: TypographyItem;
  };
};

type Theme = {
  borders: {};
  borderWidths: [];
  borderStyles: [];
  radii: [];
  breakpoints: Breakpoints;
  media: {
    grid: number;
  };
  colors: {};
  fonts: {
    noirPro: string;
  };
  fontWeights: {
    normal: number;
    regular: number;
    medium: number;
    semibold: number;
    bold: number;
    extrabold: number;
  };
  fontSizes: string[];
  lineHeights: string[];
  letterSpacings: string[];
  textDecoration: {};
  space: number[];
  sizes: [];
  shadows: {};
  transitions: [];
  zIndices: number[];
  typography: Typography;
};

const refreshTheme: Theme = {
  borders: {},
  borderWidths: [],
  borderStyles: [],
  radii: [],
  breakpoints: { } as Breakpoints,
  media: {
    grid: 1280,
  },
  colors: /* Colors, Background-colors, */ {},
  fonts: {
    noirPro: '"Noir Pro", Arial, Helvetica, sans-serif',
  },
  fontWeights: {
    // We should not be defining both normal and regular in theme. Eventually remove regular and add a light font-weight variant.
    normal: 400,
    regular: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
    extrabold: 800,
  },
  fontSizes: [
    '14px', // 0
    '16px', // 1
    '18px', // 2
    '24px', // 3
    '32px', // 4
    '36px', // 5
    '38px', // 6
    '48px', // 7
    '66px', // 8
    '12PX', // 9
    '13PX', // 10
  ],
  lineHeights: [
    '20px', // 0
    '24px', // 1
    '28px', // 2
    '36px', // 3
    '40px', // 4
    '44px', // 5
    '46px', // 6
    '58px', // 7
    '78px', // 8
  ],
  letterSpacings: ['0px'],
  textDecoration: {},
  space: /* Margin, Padding, Grid Spacing */ [
    0, 4, 8, 16, 32, 64, 128, 256, 512,
  ],
  sizes: /* Width, Height, +min, +max (min - 1) */ [],
  shadows: /* Box-Shadow, Text-Shadow */ {},
  transitions: [],
  zIndices: [0, 1, 2, 4, 6, 8, 12, 14, 16],
  typography: {} as Typography,
};

// * Typography
refreshTheme.typography = {
  mobile: {
    heading1: {
      fontFamily: refreshTheme.fonts.noirPro,
      fontWeight: refreshTheme.fontWeights.semibold,
      fontSize: refreshTheme.fontSizes[6],
      lineHeight: refreshTheme.lineHeights[6],
      letterSpacing: refreshTheme.letterSpacings[0],
    },
    heading2: {
      fontFamily: refreshTheme.fonts.noirPro,
      fontWeight: refreshTheme.fontWeights.semibold,
      fontSize: refreshTheme.fontSizes[4],
      lineHeight: refreshTheme.lineHeights[4],
      letterSpacing: refreshTheme.letterSpacings[0],
    },
    heading3: {
      fontFamily: refreshTheme.fonts.noirPro,
      fontWeight: refreshTheme.fontWeights.semibold,
      fontSize: refreshTheme.fontSizes[3],
      lineHeight: refreshTheme.lineHeights[3],
      letterSpacing: refreshTheme.letterSpacings[0],
    },
    heading4: {
      fontFamily: refreshTheme.fonts.noirPro,
      fontWeight: refreshTheme.fontWeights.semibold,
      fontSize: refreshTheme.fontSizes[3],
      lineHeight: refreshTheme.lineHeights[3],
      letterSpacing: refreshTheme.letterSpacings[0],
    },
    heading5: {
      fontFamily: refreshTheme.fonts.noirPro,
      fontWeight: refreshTheme.fontWeights.semibold,
      fontSize: refreshTheme.fontSizes[2],
      lineHeight: refreshTheme.lineHeights[1],
      letterSpacing: refreshTheme.letterSpacings[0],
    },
    heading6: {
      fontFamily: refreshTheme.fonts.noirPro,
      fontWeight: refreshTheme.fontWeights.semibold,
      fontSize: refreshTheme.fontSizes[2],
      lineHeight: refreshTheme.lineHeights[1],
      letterSpacing: refreshTheme.letterSpacings[0],
    },
    body1: {
      fontFamily: refreshTheme.fonts.noirPro,
      fontWeight: refreshTheme.fontWeights.regular,
      fontSize: refreshTheme.fontSizes[2],
      lineHeight: refreshTheme.lineHeights[1],
      letterSpacing: refreshTheme.letterSpacings[0],
    },
    body2: {
      fontFamily: refreshTheme.fonts.noirPro,
      fontWeight: refreshTheme.fontWeights.regular,
      fontSize: refreshTheme.fontSizes[1],
      lineHeight: refreshTheme.lineHeights[1],
      letterSpacing: refreshTheme.letterSpacings[0],
    },
    body3: {
      fontFamily: refreshTheme.fonts.noirPro,
      fontWeight: refreshTheme.fontWeights.regular,
      fontSize: refreshTheme.fontSizes[0],
      lineHeight: refreshTheme.lineHeights[0],
      letterSpacing: refreshTheme.letterSpacings[0],
    },
  },
  desktop: {
    heading1: {
      fontFamily: refreshTheme.fonts.noirPro,
      fontWeight: refreshTheme.fontWeights.semibold,
      fontSize: refreshTheme.fontSizes[8],
      lineHeight: refreshTheme.lineHeights[8],
      letterSpacing: refreshTheme.letterSpacings[0],
    },
    heading2: {
      fontFamily: refreshTheme.fonts.noirPro,
      fontWeight: refreshTheme.fontWeights.semibold,
      fontSize: refreshTheme.fontSizes[7],
      lineHeight: refreshTheme.lineHeights[7],
      letterSpacing: refreshTheme.letterSpacings[0],
    },
    heading3: {
      fontFamily: refreshTheme.fonts.noirPro,
      fontWeight: refreshTheme.fontWeights.semibold,
      fontSize: refreshTheme.fontSizes[5],
      lineHeight: refreshTheme.lineHeights[5],
      letterSpacing: refreshTheme.letterSpacings[0],
    },
    heading4: {
      fontFamily: refreshTheme.fonts.noirPro,
      fontWeight: refreshTheme.fontWeights.medium,
      fontSize: refreshTheme.fontSizes[3],
      lineHeight: refreshTheme.lineHeights[3],
      letterSpacing: refreshTheme.letterSpacings[0],
    },
    heading5: {
      fontFamily: refreshTheme.fonts.noirPro,
      fontWeight: refreshTheme.fontWeights.semibold,
      fontSize: refreshTheme.fontSizes[2],
      lineHeight: refreshTheme.lineHeights[1],
      letterSpacing: refreshTheme.letterSpacings[0],
    },
    heading6: {
      fontFamily: refreshTheme.fonts.noirPro,
      fontWeight: refreshTheme.fontWeights.semibold,
      fontSize: refreshTheme.fontSizes[2],
      lineHeight: refreshTheme.lineHeights[1],
      letterSpacing: refreshTheme.letterSpacings[0],
    },
    body1: {
      fontFamily: refreshTheme.fonts.noirPro,
      fontWeight: refreshTheme.fontWeights.regular,
      fontSize: refreshTheme.fontSizes[3],
      lineHeight: refreshTheme.lineHeights[2],
      letterSpacing: refreshTheme.letterSpacings[0],
    },
    body2: {
      fontFamily: refreshTheme.fonts.noirPro,
      fontWeight: refreshTheme.fontWeights.regular,
      fontSize: refreshTheme.fontSizes[2],
      lineHeight: refreshTheme.lineHeights[1],
      letterSpacing: refreshTheme.letterSpacings[0],
    },
    body3: {
      fontFamily: refreshTheme.fonts.noirPro,
      fontWeight: refreshTheme.fontWeights.regular,
      fontSize: refreshTheme.fontSizes[1],
      lineHeight: refreshTheme.lineHeights[1],
      letterSpacing: refreshTheme.letterSpacings[0],
    },
    body4: {
      fontFamily: refreshTheme.fonts.noirPro,
      fontWeight: refreshTheme.fontWeights.regular,
      fontSize: refreshTheme.fontSizes[9],
      lineHeight: refreshTheme.lineHeights[1],
      letterSpacing: refreshTheme.letterSpacings[0],
    },
    body5: {
      fontFamily: refreshTheme.fonts.noirPro,
      fontWeight: refreshTheme.fontWeights.regular,
      fontSize: refreshTheme.fontSizes[10],
      lineHeight: refreshTheme.lineHeights[1],
      letterSpacing: refreshTheme.letterSpacings[0],
    },
    link1: {
      fontFamily: refreshTheme.fonts.noirPro,
      fontWeight: refreshTheme.fontWeights.normal,
      fontSize: refreshTheme.fontSizes[0],
      lineHeight: refreshTheme.lineHeights[3],
      letterSpacing: refreshTheme.letterSpacings[0],
    },
  },
};

// * Breakpoints
// 320, 375, 508, 550, 665, 700, 768, 916, 1279, 1399, 1440
refreshTheme.breakpoints.min = {
  sm: '374px',
  md: '678px',
  lg: '959px',
  xl: '1279px',
};

refreshTheme.breakpoints.max = {
  sm: '375px',
  md: '679px',
  lg: '960px',
  xl: '1280px',
};

export default refreshTheme;
